import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '-'; }

export function generateInvoicePDF(invoice) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.width;

  // Purple header bar
  doc.setFillColor(115, 103, 240);
  doc.rect(0, 0, w, 40, 'F');
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.text('INVOICE', 16, 20);
  doc.setFontSize(10);
  doc.text(invoice.invoice_number || 'INV-000000', 16, 30);
  doc.setFontSize(10);
  doc.text('RoofMate', w - 16, 20, { align: 'right' });
  doc.setFontSize(8);
  doc.text('Roof Measurement & Mapping Platform', w - 16, 28, { align: 'right' });

  let y = 52;

  // Bill To / Invoice Details
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text('BILLED TO', 16, y);
  doc.text('INVOICE DETAILS', 120, y);
  y += 6;
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text(invoice.tenant_name || 'Customer', 16, y);
  doc.text(`Invoice No: ${invoice.invoice_number || '-'}`, 120, y);
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(invoice.tenant_email || '', 16, y);
  doc.text(`Date: ${fmtDate(invoice.paid_at || invoice.created_at)}`, 120, y);
  y += 5;
  doc.text(`Status: Paid`, 120, y);
  y += 5;
  doc.text(`Payment By: ${invoice.created_by || invoice.metadata?.admin_email || '-'}`, 120, y);

  y += 12;

  // Line items table
  autoTable(doc, {
    startY: y,
    head: [['Description', 'Plan', 'Period', 'Amount']],
    body: [
      [
        `${invoice.plan_name || invoice.plan_id || 'Subscription'} Plan`,
        (invoice.plan_id || '').charAt(0).toUpperCase() + (invoice.plan_id || '').slice(1),
        'Monthly',
        `$${(invoice.amount || 0).toFixed(2)}`
      ]
    ],
    theme: 'grid',
    headStyles: { fillColor: [115, 103, 240], fontSize: 9, cellPadding: 4, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, cellPadding: 4 },
    columnStyles: { 0: { cellWidth: 80 }, 3: { halign: 'right', cellWidth: 30 } },
    margin: { left: 16, right: 16 },
  });

  y = doc.lastAutoTable.finalY + 4;

  // Totals
  doc.setFillColor(248, 247, 252);
  doc.rect(w - 90, y, 74, 24, 'F');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('Subtotal:', w - 86, y + 8);
  doc.text('Total:', w - 86, y + 18);
  doc.setTextColor(40, 40, 40);
  doc.setFontSize(9);
  doc.text(`$${(invoice.amount || 0).toFixed(2)}`, w - 20, y + 8, { align: 'right' });
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(`$${(invoice.amount || 0).toFixed(2)}`, w - 20, y + 19, { align: 'right' });
  doc.setFont(undefined, 'normal');

  y += 36;

  // Plan details
  if (invoice.plan_details) {
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text('PLAN INCLUDES', 16, y);
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    const mp = invoice.plan_details.max_projects;
    const mu = invoice.plan_details.max_users;
    doc.text(`${mp >= 999999 ? 'Unlimited' : mp} Projects, ${mu >= 999999 ? 'Unlimited' : mu} Users`, 16, y);
  }

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.text('Thank you for choosing RoofMate.', 16, doc.internal.pageSize.height - 16);
  doc.text(`Generated on ${new Date().toLocaleDateString('en-GB')}`, 16, doc.internal.pageSize.height - 10);
  doc.text('RoofMate - Roof Measurement & Mapping Platform', w - 16, doc.internal.pageSize.height - 10, { align: 'right' });

  doc.save(`${invoice.invoice_number || 'invoice'}.pdf`);
}

export function generateMeasurementPDF(project, measurements) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.width;

  // Header
  doc.setFillColor(115, 103, 240);
  doc.rect(0, 0, w, 36, 'F');
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.text('Measurement Report', 16, 18);
  doc.setFontSize(9);
  doc.text('RoofMate', w - 16, 14, { align: 'right' });
  doc.text(new Date().toLocaleDateString('en-GB'), w - 16, 22, { align: 'right' });

  let y = 46;

  // Project info
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text('PROJECT DETAILS', 16, y);
  y += 7;
  doc.setFontSize(11);
  doc.setTextColor(40, 40, 40);
  doc.text(project.name || 'Untitled Project', 16, y);
  y += 6;
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  if (project.address) { doc.text(`Address: ${project.address}`, 16, y); y += 5; }
  if (project.description) { doc.text(`Description: ${project.description}`, 16, y); y += 5; }
  if (project.tenant) { doc.text(`Organization: ${project.tenant}`, 16, y); y += 5; }
  if (project.created_by) { doc.text(`Created by: ${project.created_by}`, 16, y); y += 5; }
  doc.text(`Date: ${fmtDate(project.created_at)}`, 16, y);
  y += 10;

  // Summary
  const areas = measurements.filter(m => m.measurement_type === 'area');
  const distances = measurements.filter(m => m.measurement_type === 'distance');
  const totalArea = areas.reduce((s, m) => s + (m.value || 0), 0);
  const totalDist = distances.reduce((s, m) => s + (m.value || 0), 0);

  doc.setFillColor(248, 247, 252);
  doc.roundedRect(16, y, w - 32, 20, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(115, 103, 240);
  doc.text('SUMMARY', 22, y + 6);
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(`Total Measurements: ${measurements.length}`, 22, y + 14);
  doc.text(`Total Area: ${totalArea.toFixed(2)} m\u00B2`, 85, y + 14);
  doc.text(`Total Distance: ${totalDist.toFixed(2)} m`, 148, y + 14);
  y += 28;

  // Measurements table
  if (measurements.length > 0) {
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text('MEASUREMENTS', 16, y);
    y += 3;

    const body = measurements.map((m, i) => [
      (i + 1).toString(),
      m.label || `Measurement ${i + 1}`,
      (m.shape_type || m.measurement_type || '-').charAt(0).toUpperCase() + (m.shape_type || m.measurement_type || '-').slice(1),
      m.value ? `${m.value.toFixed(2)} ${m.unit || ''}` : '-',
      (m.details || '').replace(/\n/g, ', ').substring(0, 60),
    ]);

    autoTable(doc, {
      startY: y,
      head: [['#', 'Label', 'Type', 'Value', 'Details']],
      body,
      theme: 'grid',
      headStyles: { fillColor: [115, 103, 240], fontSize: 8, cellPadding: 3, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7.5, cellPadding: 2.5 },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 35 }, 2: { cellWidth: 25 }, 3: { cellWidth: 30 }, 4: { cellWidth: 'auto' } },
      margin: { left: 16, right: 16 },
    });
  }

  // Footer
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text(`RoofMate Measurement Report - ${project.name || 'Project'}  |  Page ${i} of ${pages}`, 16, doc.internal.pageSize.height - 10);
  }

  doc.save(`roofmate-measurements-${(project.name || 'project').replace(/\s+/g, '-').toLowerCase()}.pdf`);
}
